/**
 * Background WebSocket notifications — prints events to stderr
 * so they don't interfere with piped stdout.
 *
 * Enabled via `arbi config notifications on`.
 * Started automatically by resolveWorkspace() when enabled.
 */

import {
  connectWithReconnect,
  formatWsMessage,
  type ReconnectableWsConnection,
  type MessageLevel,
} from '@arbidocs/sdk'
import { isMessageType, type WsResponseCompleteMessage } from '@arbidocs/client'
import { updateTaskStatus } from './task-store.js'
import { chalk } from './output.js'

let activeConnection: ReconnectableWsConnection | null = null

function timestamp(): string {
  return new Date().toLocaleTimeString('en-GB', { hour12: false })
}

function colorize(level: MessageLevel, text: string): string {
  if (level === 'success') return chalk.green(text)
  if (level === 'error') return chalk.red(text)
  if (level === 'warning') return chalk.yellow(text)
  return text
}

/**
 * Start background WebSocket notifications.
 * Prints to stderr so stdout stays clean for command output.
 * Safe to call multiple times — only one connection is kept.
 */
export async function startBackgroundNotifications(
  baseUrl: string,
  accessToken: string
): Promise<void> {
  if (activeConnection) return

  try {
    activeConnection = await connectWithReconnect({
      baseUrl,
      accessToken,
      onMessage: (msg) => {
        // Sync task store when background tasks complete
        if (isMessageType<WsResponseCompleteMessage>(msg, 'response_complete')) {
          updateTaskStatus(msg.response_id, msg.status)
        }

        const { text, level } = formatWsMessage(msg)
        process.stderr.write(`\n${colorize(level, `[${timestamp()}] ${text}`)}\n`)
      },
      onClose: () => {
        activeConnection = null
      },
      onReconnecting: (attempt, maxRetries) => {
        process.stderr.write(
          `\n${chalk.yellow(`[${timestamp()}] Reconnecting... (${attempt}/${maxRetries})`)}\n`
        )
      },
      onReconnected: () => {
        process.stderr.write(`\n${chalk.green(`[${timestamp()}] Reconnected`)}\n`)
      },
      onReconnectFailed: () => {
        process.stderr.write(`\n${chalk.red(`[${timestamp()}] Reconnection failed`)}\n`)
      },
    })
  } catch {
    // Silent failure — notifications are best-effort
  }
}

export function stopBackgroundNotifications(): void {
  activeConnection?.close()
  activeConnection = null
}

// Clean up on exit
process.on('exit', stopBackgroundNotifications)
process.on('SIGINT', () => {
  stopBackgroundNotifications()
  process.exit(0)
})
